// Catégories Outlook posées sur le mail d'origine après un rangement : « Rangé »,
// et le NUMÉRO D'AFFAIRE du projet de destination.
//
// POURQUOI CECI NE PASSE PAS PAR MICROSOFT GRAPH. On pourrait croire qu'étiqueter
// un mail qui n'est PAS celui ouvert dans le volet de lecture — le cas de la
// multi-sélection — impose Graph, donc un scope `Mail.ReadWrite` et un
// consentement administrateur. Ce n'est pas le cas : `loadItemByIdAsync`
// (Mailbox 1.15, déjà exigé par le manifeste et déjà utilisé pour lire le .eml)
// rend un `Office.LoadedMessageRead` qui expose `categories`. L'add-in a donc
// tout ce qu'il faut avec la permission qu'il possède déjà
// (`Mailbox.ReadWrite.User` = read/write mailbox). Aucun nouveau consentement.
//
// LE CAS DU GLISSER-DÉPOSER, résolu par un détour. Outlook livre le mail déposé
// comme un blob .eml sans aucun identifiant (cf. services/office/dragDrop.ts) :
// `applyFiledCategories` ne peut donc pas être appelée directement. Le blob est
// d'abord rattaché à son élément d'origine en comparant le Message-ID de ses
// en-têtes à ceux de la sélection Outlook — voir messageIdentity.ts, qui explique
// pourquoi la sélection seule ne suffit pas comme preuve. Reste hors d'atteinte
// par ce chemin : un .msg (binaire, illisible côté client) et tout fichier déposé
// depuis l'explorateur, qui ne correspond à aucun mail de la boîte.
//
// CE QUI RESTE HORS D'ATTEINTE, et pourquoi :
//  - le MODE RÉDACTION sur Outlook web et le nouvel Outlook : Microsoft y
//    interdit explicitement la gestion des catégories. Sans effet ici (on
//    étiquette des mails reçus), mais à savoir pour « Envoyer et ranger ».
//  - la PERMISSION du manifeste : `masterCategories` exige « read/write mailbox »
//    (`Mailbox.ReadWrite.User`) et `item.categories.addAsync` « read/write item ».
//    Le manifeste déclarait les DEUX niveaux à la fois — `MailboxItem.Read.User`
//    ET `Mailbox.ReadWrite.User` — alors qu'ils sont cumulatifs (« read/write
//    mailbox » contient déjà « read item ») et que Microsoft n'en documente qu'un
//    seul. Un hôte qui retiendrait le plus restrictif refuserait TOUT ce fichier.
//    Le niveau bas a donc été retiré : il n'apportait rien et pouvait tout casser.
import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';
import { reportError } from '../errors/errorReporter';

/** Nom exact de la catégorie. Il sert AUSSI de clé dans la liste maîtresse. */
export const FILED_CATEGORY_NAME = 'Rangé';

/**
 * Un numéro d'affaire n'est retenu comme nom de catégorie que s'il fait CINQ
 * CHIFFRES, comme l'énonce la demande (« format 00000 »).
 *
 * CE FILTRE N'EST PAS DÉFENSIF, il est nécessaire — mesuré sur la base de
 * production : sur 140 projets, 16 portent dans `containers.number` un code qui
 * n'est pas un numéro d'affaire, mais un intitulé interne (`NC`, `BIM`, `CSE`,
 * `MODELE`, `xxxxx`, `Veille`, `Groupe Bas Carbone`…). Sans le filtre, ranger un
 * mail dans l'un d'eux créerait dans la boîte de l'utilisateur une catégorie
 * « Groupe Bas Carbone » — et la liste maîtresse d'une boîte est une ressource
 * PARTAGÉE par tous ses dossiers et bornée en taille : ce qu'on y écrit à tort y
 * reste, et se retire à la main.
 *
 * Les espaces d'équipe, eux, n'ont pas de numéro du tout (mesuré : 0 sur 12) :
 * l'absence est donc le cas NORMAL, pas une anomalie à signaler.
 */
const PROJECT_NUMBER_RE = /^\d{5}$/;

export function projectNumberCategory(number: string | null | undefined): string | null {
  const trimmed = number?.trim() ?? '';
  return PROJECT_NUMBER_RE.test(trimmed) ? trimmed : null;
}

// Preset4 = Green (Office.MailboxEnums.CategoryColor), réservé à « Rangé ».
// La valeur littérale sert de repli parce que `Office.MailboxEnums` n'existe pas
// hors d'Outlook — et les valeurs de l'énumération SONT ces chaînes.
const GREEN = 'Preset4';
// 25 presets (Preset0…Preset24), dont un est pris par le vert de « Rangé ».
const PRESET_COUNT = 25;

interface CategoriesApi {
  addAsync: (names: string[], cb: (r: Office.AsyncResult<void>) => void) => void;
}

interface LoadedItemWithCategories {
  categories?: CategoriesApi;
  unloadAsync?: (cb: (r: Office.AsyncResult<void>) => void) => void;
}

function mailbox(): typeof Office.context.mailbox | null {
  const office = (globalThis as { Office?: typeof Office }).Office;
  return office?.context?.mailbox ?? null;
}

function presetColor(preset: string): string {
  const office = (globalThis as { Office?: typeof Office }).Office;
  const value = (
    office as unknown as { MailboxEnums?: { CategoryColor?: Record<string, string> } } | undefined
  )?.MailboxEnums?.CategoryColor?.[preset];
  return value ?? preset;
}

/**
 * Couleur à donner à une catégorie qu'on doit CRÉER dans la liste maîtresse.
 *
 * Jamais appliquée à une catégorie qui existe déjà : `ensureCategories` lit la
 * liste avant d'écrire, donc une couleur choisie par l'utilisateur pour « 19048 »
 * ne sera pas écrasée.
 *
 * DÉTERMINISTE et dérivée du numéro lui-même, plutôt que « sans couleur » : un
 * projet garde ainsi la même teinte d'une session à l'autre et d'un poste à
 * l'autre, ce qui rend la liste de mails lisible d'un coup d'œil. Le vert est
 * exclu du tirage : une catégorie de numéro ne doit pas pouvoir se confondre avec
 * « Rangé ». Avec 24 teintes pour 124 projets les collisions sont certaines et
 * sans conséquence — deux projets de même couleur restent deux libellés distincts.
 */
function colorFor(name: string): string {
  if (name === FILED_CATEGORY_NAME) return presetColor(GREEN);
  const parsed = Number.parseInt(name, 10);
  const slot = (Number.isFinite(parsed) ? parsed : 0) % (PRESET_COUNT - 1);
  // Saute Preset4 pour ne jamais retomber sur le vert.
  const preset = slot >= 4 ? slot + 1 : slot;
  return presetColor(`Preset${preset}`);
}

/**
 * Garantit que chaque catégorie demandée existe dans la liste maîtresse.
 *
 * INDISPENSABLE : `categories.addAsync` échoue avec `InvalidCategory` si la
 * catégorie n'est pas déjà dans cette liste — « chaque catégorie doit figurer
 * dans la liste maîtresse de la boîte ». C'est le piège de cette API.
 *
 * UNE SEULE LECTURE DE LA LISTE ET UNE SEULE ÉCRITURE POUR TOUT LE JEU, et non
 * une paire par catégorie : les deux API acceptent des tableaux. Sonder « Rangé »
 * puis « 19048 » séparément doublerait les allers-retours vers Outlook à chaque
 * lot, pour la même information.
 *
 * MÉMOÏSATION ASYMÉTRIQUE, et c'est le point délicat :
 *  - un SUCCÈS est gardé indéfiniment (la catégorie ne disparaîtra pas) ;
 *  - un ÉCHEC n'est gardé que le temps d'un LOT, via `resetCategoryProbes`.
 *
 * Les deux extrêmes sont mauvais. Ne rien mémoïser fait relire la liste
 * maîtresse pour chacun des vingt mails d'une multi-sélection. Mémoïser l'échec
 * pour toute la session condamne la catégorisation jusqu'au rechargement du
 * volet dès la première panne — or toutes ne sont pas définitives : un refus de
 * délégué sur boîte partagée l'est, un Office pas encore prêt ou un aléa réseau
 * ne l'est pas.
 *
 * La mémoïsation est par JEU de catégories et non globale : un lot rangé dans le
 * projet 19048 puis un autre dans 20100 ne demandent pas la même chose. Les deux
 * jeux partagent en revanche « Rangé », dont la présence est de toute façon
 * reconfirmée par la lecture de la liste.
 */
export interface CategoryProbe {
  ok: boolean;
  /** Motif technique de l'échec. Présent seulement quand `ok` est faux. */
  reason?: string;
}

const probes = new Map<string, Promise<CategoryProbe>>();
// Issue DÉJÀ CONNUE de chaque sonde ; une clé absente = sonde encore en vol.
// Suivie à part des promesses pour que la remise à zéro soit SYNCHRONE — la lire
// depuis `probe.then()` la repousserait d'une micro-tâche, et le premier mail du
// lot pourrait encore voir l'ancienne mémoïsation.
const outcomes = new Map<string, boolean>();

function probeKey(names: string[]): string {
  // Trié : l'ordre des catégories n'a aucun sens ici, et deux appels qui
  // demandent la même chose dans un autre ordre doivent partager la sonde.
  // Séparateur nul, qu'un nom de catégorie Outlook ne peut pas contenir.
  return [...names].sort().join('\u0000');
}

/**
 * À appeler UNE FOIS par lot de rangement, avant le premier mail.
 *
 * Oublie les tentatives INFRUCTUEUSES pour que le lot suivant réessaie, et
 * conserve les succès. Sans cela, la mémoïsation d'un échec transitoire
 * survivrait à toute la session.
 */
export function resetCategoryProbes(): void {
  for (const [key, ok] of outcomes) {
    if (!ok) {
      probes.delete(key);
      outcomes.delete(key);
    }
  }
}

/**
 * Motif lisible d'un échec Office, NOM D'ERREUR COMPRIS.
 *
 * C'est le nom qui porte l'information décisive, et lui seul : `PermissionDenied`
 * accuse le manifeste ou une boîte déléguée, `InvalidCategory` accuse la liste
 * maîtresse. Le message seul — souvent un « An internal error occurred » — ne
 * permet de trancher ni l'un ni l'autre.
 */
function officeReason(
  error: { name?: string; message?: string } | undefined,
  fallback: string,
): string {
  const name = error?.name?.trim();
  const message = error?.message?.trim();
  if (name && message) return `${name} — ${message}`;
  return name || message || fallback;
}

export function ensureCategories(names: string[]): Promise<CategoryProbe> {
  const key = probeKey(names);
  const memo = probes.get(key);
  if (memo) return memo;

  const probe = (async (): Promise<CategoryProbe> => {
    const mb = mailbox() as unknown as {
      masterCategories?: {
        getAsync: (cb: (r: Office.AsyncResult<{ displayName: string }[]>) => void) => void;
        addAsync: (
          cats: { displayName: string; color: string }[],
          cb: (r: Office.AsyncResult<void>) => void,
        ) => void;
      };
    } | null;
    if (!mb?.masterCategories) {
      return {
        ok: false,
        reason:
          'API masterCategories absente — permission « read/write mailbox » manquante, ou Mailbox < 1.8',
      };
    }

    const existing = await new Promise<{ list: { displayName: string }[] | null; reason?: string }>(
      (resolve) => {
        mb.masterCategories!.getAsync((r) =>
          r.status === Office.AsyncResultStatus.Succeeded
            ? resolve({ list: r.value ?? [] })
            : resolve({
                list: null,
                reason: officeReason(r.error, 'masterCategories.getAsync a échoué'),
              }),
        );
      },
    );
    if (existing.list === null) return { ok: false, reason: existing.reason };

    const present = new Set(existing.list.map((c) => c.displayName));
    const missing = names.filter((n) => !present.has(n));
    if (missing.length === 0) return { ok: true };

    return new Promise<CategoryProbe>((resolve) => {
      mb.masterCategories!.addAsync(
        missing.map((n) => ({ displayName: n, color: colorFor(n) })),
        (r) => {
          if (r.status === Office.AsyncResultStatus.Succeeded) return resolve({ ok: true });
          // `DuplicateCategory` = une autre fenêtre l'a créée entre-temps : c'est
          // un succès, pas une panne. `PermissionDenied` = boîte déléguée.
          const msg = r.error?.message ?? '';
          const duplicate = /duplicate/i.test(r.error?.name ?? '') || /duplicate/i.test(msg);
          addBreadcrumb({
            category: 'office.event',
            message: duplicate ? 'master-category already there' : 'master-category add failed',
            data: { name: r.error?.name, count: missing.length },
          });
          resolve(
            duplicate
              ? { ok: true }
              : { ok: false, reason: officeReason(r.error, 'masterCategories.addAsync a échoué') },
          );
        },
      );
    });
  })().then(
    (result) => {
      outcomes.set(key, result.ok);
      return result;
    },
    (err) => {
      // Une exception inattendue compte comme un échec : le lot suivant
      // réessaiera, au lieu de rester bloqué sur une promesse rejetée.
      outcomes.set(key, false);
      throw err;
    },
  );

  probes.set(key, probe);
  return probe;
}

/**
 * Fabrique l'erreur à lever ET la SIGNALE au serveur.
 *
 * POURQUOI SIGNALER, alors que l'appelant affiche déjà un motif. Ces échecs ne
 * produisaient qu'une MIETTE de contexte, et une miette ne quitte le navigateur
 * qu'attachée à un rapport d'erreur : comme le rangement, lui, réussit, aucun
 * rapport n'était jamais émis. « La catégorie ne s'applique pas » n'avait donc
 * AUCUNE trace côté serveur — rien à lire, rien à diagnostiquer.
 *
 * Gravité `warning` et `isHandled` : le rangement a bien eu lieu, seule
 * l'étiquette manque. La signature de dédoublonnage d'`errorReporter` (nom +
 * fichier + ligne) suffit à n'en envoyer qu'un par lot, et non vingt.
 */
function fail(message: string): Error {
  const error = new Error(message);
  error.name = 'FiledCategoryError';
  reportError({ error, severity: 'warning', isHandled: true, tags: ['category'] });
  return error;
}

function addTo(api: CategoriesApi, names: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    api.addAsync(names, (r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(fail(officeReason(r.error, 'categories.addAsync a échoué')));
    });
  });
}

/**
 * Pose les catégories `names` sur le message d'`itemId`. Rejette si l'étiquetage
 * n'a pas pu être écrit — l'appelant décide quoi en dire.
 *
 * TOUT OU RIEN, et c'est voulu : un seul `addAsync` porte le jeu complet, donc
 * soit les deux étiquettes arrivent, soit aucune. Deux appels séparés
 * permettraient l'état bâtard « numéro sans Rangé », que rien dans l'IHM ne sait
 * expliquer.
 *
 * À N'APPELER QU'APRÈS un rangement réussi : la catégorie est une AFFIRMATION
 * (« ce mail est dans l'outil »). L'apposer pendant la lecture du .eml, dans la
 * même fenêtre de chargement — ce qui aurait économisé un aller-retour — la
 * poserait aussi sur les mails dont l'envoi au serveur échoue ensuite, et
 * l'utilisateur croirait rangé un mail qui ne l'est pas.
 */
export async function applyFiledCategories(itemId: string, names: string[]): Promise<void> {
  if (names.length === 0) return;

  const mb = mailbox();
  if (!mb) throw fail('Office.context.mailbox indisponible');

  const probe = await ensureCategories(names);
  if (!probe.ok) {
    const listed = names.map((n) => `« ${n} »`).join(', ');
    throw fail(
      `catégories ${listed} indisponibles sur cette boîte : ${probe.reason ?? 'raison inconnue'}`,
    );
  }

  // Chemin rapide : le mail visé est celui déjà ouvert. Recommandation
  // Microsoft — ne pas passer par `loadItemByIdAsync` quand l'élément courant
  // expose déjà ce qu'il faut.
  const current = mb.item as unknown as { itemId?: string; categories?: CategoriesApi } | undefined;
  if (current?.itemId === itemId && current.categories) {
    await addTo(current.categories, names);
    return;
  }

  const loader = mb as unknown as {
    loadItemByIdAsync?: (
      id: string,
      cb: (r: Office.AsyncResult<LoadedItemWithCategories>) => void,
    ) => void;
  };
  if (!loader.loadItemByIdAsync) {
    throw fail('loadItemByIdAsync indisponible (Mailbox < 1.15)');
  }

  const loaded = await new Promise<LoadedItemWithCategories>((resolve, reject) => {
    loader.loadItemByIdAsync!(itemId, (r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
      else reject(fail(officeReason(r.error, 'loadItemByIdAsync a échoué')));
    });
  });
  try {
    if (!loaded.categories) throw fail('categories absent de l’élément chargé');
    await addTo(loaded.categories, names);
  } finally {
    // Obligatoire, et dans un `finally` : un seul élément peut être chargé à la
    // fois, donc un échec non déchargé bloquerait TOUS les mails suivants du lot.
    await new Promise<void>((resolve) => {
      if (loaded.unloadAsync) loaded.unloadAsync(() => resolve());
      else resolve();
    });
  }
}
